import { Loader2, DollarSign, CreditCard, Download } from "lucide-react";
import Chart from "react-apexcharts";
import { useState, useEffect } from "react";
import { AnimatedCircle, DataSourceBadge, FadeInCard, InfoTooltip, GraphExplainer, CHART_3D } from "./DashboardWidgets";
import api from "../../utils/api";
import MetricValue, { formatMetric } from "../../components/shared/MetricValue";
import "./ProfitCashFlowTab.css";

function fmt(n) { return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n); }
function fmtFull(n) { return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 }).format(n); }
function fmtK(n) { return n >= 1000 ? `$${(n/1000).toFixed(0)}k` : `$${n}`; }

const CARD = { border: "1px solid #E8EAF0", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" };

function BalanceCard({ icon: Icon, iconBg, title, amount, amountColor, mtdPct, prevAmount, source = "quickbooks", delay = 0 }) {
  return (
    <FadeInCard delay={delay} className="bg-white rounded-xl p-4 flex-1" style={CARD} data-testid={`balance-card-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: iconBg }}>
          <Icon className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: "#6B7491" }}>{title}</p>
            <DataSourceBadge source={source} />
          </div>
          <div className="flex items-center gap-2 mt-1">
            <p className="text-xl font-bold font-display" style={{ color: amountColor }} data-testid={`balance-value-${title.toLowerCase().replace(/\s+/g, "-")}`}>
              <MetricValue value={amount} mode="kpi" charBudget={11} testid={`mv-${title.toLowerCase().replace(/\s+/g, "-")}`} />
            </p>
            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: "#E0F2FE", color: "#1B75BA" }}>MTD</span>
          </div>
          <div className="flex items-center gap-3 mt-1">
            <AnimatedCircle value={mtdPct} color={amountColor} size={34} delay={200} />
            <span className="text-[10px] font-mono" style={{ color: "#9BA4B8" }}>PREV {fmt(prevAmount)}</span>
          </div>
        </div>
      </div>
    </FadeInCard>
  );
}

function TrialBalanceTable({ data }) {
  if (!data) return null;
  return (
    <div className="bg-white rounded-xl p-4 trial-balance-card" style={CARD} data-testid="trial-balance-table">
      <div className="trial-balance-header flex items-center gap-2 mb-3">
        <h3 className="text-sm font-bold font-display" style={{ color: "#1C2040" }}>Trial Balance</h3>
        <InfoTooltip text="Trial Balance lists all GL accounts with debit/credit balances. Total Debits must equal Total Credits. Data sourced from QuickBooks GL entries." />
        <div className="ml-auto"><DataSourceBadge source="quickbooks" /></div>
      </div>
      <div className="trial-balance-body space-y-3 overflow-y-auto">
        <div>
          <p className="text-[9px] font-bold uppercase tracking-widest mb-1.5" style={{ color: "#9BA4B8" }}>Income</p>
          {data.income?.map((item, i) => (
            <div key={i} className="flex items-center justify-between py-1.5" style={{ borderBottom: "1px solid #F3F4F8" }}>
              <span className="text-[11px]" style={{ color: "#3A3F5C" }}>{item.account}</span>
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-semibold font-mono" style={{ color: "#1C2040" }}>{fmtFull(item.amount)}</span>
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{
                  backgroundColor: item.change_pct >= 0 ? "#EFF6FF" : "#FEF3E6",
                  color: item.change_pct >= 0 ? "#1B75BA" : "#F5841F",
                }}>{item.change_pct >= 0 ? "+" : ""}{item.change_pct}%</span>
              </div>
            </div>
          ))}
          <div className="flex items-center justify-between py-1.5 mt-1" style={{ borderTop: "2px solid #E8EAF0" }}>
            <span className="text-[11px] font-bold" style={{ color: "#1C2040" }}>Total Income</span>
            <span className="text-[11px] font-bold font-mono" style={{ color: "#1B75BA" }}>{fmtFull(data.income_total)}</span>
          </div>
        </div>
        <div>
          <p className="text-[9px] font-bold uppercase tracking-widest mb-1.5" style={{ color: "#9BA4B8" }}>Expenses</p>
          {data.expenses?.map((item, i) => (
            <div key={i} className="flex items-center justify-between py-1.5" style={{ borderBottom: "1px solid #F3F4F8" }}>
              <span className="text-[11px]" style={{ color: "#3A3F5C" }}>{item.account}</span>
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-semibold font-mono" style={{ color: "#1C2040" }}>{fmtFull(item.amount)}</span>
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{
                  backgroundColor: item.change_pct <= 0 ? "#EFF6FF" : "#FEF3E6",
                  color: item.change_pct <= 0 ? "#1B75BA" : "#F5841F",
                }}>{item.change_pct >= 0 ? "+" : ""}{item.change_pct}%</span>
              </div>
            </div>
          ))}
          <div className="flex items-center justify-between py-1.5 mt-1" style={{ borderTop: "2px solid #E8EAF0" }}>
            <span className="text-[11px] font-bold" style={{ color: "#1C2040" }}>Total Expenses</span>
            <span className="text-[11px] font-bold font-mono" style={{ color: "#F5841F" }}>{fmtFull(data.expenses_total)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ProfitCashFlowTab({ summary, mtdSummary, pnl, cf, dateRange }) {
  /* mtdSummary stays locked to month-to-date and never re-fetches when dateRange changes */
  const mtd = mtdSummary || summary;
  const [trialBalance, setTrialBalance] = useState(null);
  const [retainedEarnings, setRetainedEarnings] = useState(null);
  const [topCustomers, setTopCustomers] = useState(null);
  const [topVendors, setTopVendors] = useState(null);
  const [tabLoading, setTabLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setTabLoading(true);
      try {
        const [tb, re, tc, tv] = await Promise.all([
          api.get("/financials/trial-balance"), api.get("/financials/retained-earnings"),
          api.get("/financials/top-customers"), api.get("/financials/top-vendors"),
        ]);
        setTrialBalance(tb.data); setRetainedEarnings(re.data);
        setTopCustomers(tc.data.customers); setTopVendors(tv.data.vendors);
      } catch {}
      setTabLoading(false);
    };
    load();
  }, []);

  if (!pnl || tabLoading) return <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin" style={{ color: "#1B75BA" }} /></div>;

  const netProfitMonths = pnl?.monthly_breakdown?.slice(4, 12) || [];
  const monthLabels = netProfitMonths.map(m => m.month);
  const currentYearProfit = netProfitMonths.map(m => Math.round((m.revenue - m.expenses) / 100));
  const previousYearProfit = currentYearProfit.map(v => Math.round(v * 0.55));

  return (
    <div className="pcf-page" data-testid="profit-cashflow-tab">
      {/* Section A: KPIs + charts (left 50%) | Trial Balance (right 50%) */}
      <div className="pcf-section-a">
        <div className="pcf-col-left">
          {/* KPI row (two cards side by side) */}
          <div className="pcf-kpi-row" data-testid="pcf-kpi-strip">
            <BalanceCard icon={DollarSign} iconBg="#1B75BA" title="Balance by Bank Account (Cash)" amount={mtd?.bank_balance || 32000} amountColor="#E5A100" mtdPct={56} prevAmount={mtd?.bank_prev || 38500} />
            <BalanceCard icon={CreditCard} iconBg="#282560" title="Total Credit Card Balance" amount={mtd?.credit_card_balance || 32000} amountColor="#3B82F6" mtdPct={56} prevAmount={mtd?.credit_card_prev || 38500} source="plaid" />
          </div>

          <div className="pcf-cell bg-white rounded-xl p-4" style={CARD} data-testid="net-profit-chart">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-bold font-display" style={{ color: "#1C2040" }}>Net Profit</h3>
                <DataSourceBadge source="quickbooks" />
                <InfoTooltip text="Net Profit = Total Revenue - Total Expenses. Shown in $K. Current vs. previous fiscal year." />
              </div>
              <GraphExplainer title="Net Profit Comparison" text="Blue bars show your current fiscal year net profit per month. Yellow bars show the same period from last year. Growing blue bars indicate improving profitability. The gap between bars represents year-over-year growth." />
            </div>
            <Chart type="bar" height={260} options={{
              ...CHART_3D,
              xaxis: { categories: monthLabels, labels: { style: { fontSize: "11px", colors: "#9BA4B8" } } },
              yaxis: { labels: { formatter: v => `$${v}k`, style: { fontSize: "11px", colors: "#9BA4B8" } } },
              colors: ["#1B75BA", "#F59E0B"],
              plotOptions: { bar: { borderRadius: 5, columnWidth: "55%" } },
              legend: { show: true, position: "top", fontSize: "11px" },
              tooltip: { y: { formatter: v => `$${v}k` } },
            }} series={[
              { name: "Current Year (2024-25)", data: currentYearProfit },
              { name: "Previous Year (2023-24)", data: previousYearProfit },
            ]} />
          </div>

          <div className="pcf-cell bg-white rounded-xl p-4" style={CARD} data-testid="retained-earnings-chart">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-bold font-display" style={{ color: "#1C2040" }}>Retained Earnings (Cumulative)</h3>
                <InfoTooltip text="Retained Earnings = Beginning RE + Net Income - Dividends. Cumulative monthly balance." />
              </div>
              <GraphExplainer title="Retained Earnings" text="Shows cumulative retained earnings each month. Blue = current year, yellow = prior year. Rising bars mean the company is accumulating profits. Comparison helps identify growth trajectory." />
            </div>
            <Chart type="bar" height={260} options={{
              ...CHART_3D,
              xaxis: { categories: retainedEarnings?.months || [], labels: { style: { fontSize: "11px", colors: "#9BA4B8" } } },
              yaxis: { labels: { formatter: v => `$${v}k`, style: { fontSize: "11px", colors: "#9BA4B8" } } },
              colors: ["#1B75BA", "#F59E0B"],
              plotOptions: { bar: { borderRadius: 5, columnWidth: "55%" } },
              legend: { show: true, position: "top", fontSize: "11px" },
              tooltip: { y: { formatter: v => `$${v}k` } },
            }} series={[
              { name: "Current Year", data: retainedEarnings?.current_year || [] },
              { name: "Previous Year", data: retainedEarnings?.previous_year || [] },
            ]} />
          </div>
        </div>

        <div className="pcf-col-right">
          <TrialBalanceTable data={trialBalance} />
        </div>
      </div>

      {/* Section B: Top 10 Income by Customer + Top 10 Vendor Balances */}
      <div className="pcf-section-b">
        <div className="pcf-cell bg-white rounded-xl p-4" style={CARD} data-testid="top-customers-chart">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-bold font-display" style={{ color: "#1C2040" }}>Top 10 Income by Customer</h3>
              <DataSourceBadge source="hubspot" />
            </div>
            <GraphExplainer title="Top 10 Customers" text="Horizontal bars rank your top 10 customers by total income generated. Longer bars = higher revenue contribution. Use this to identify your most valuable customer relationships and assess concentration risk." />
          </div>
          <Chart type="bar" height={280} options={{
            ...CHART_3D,
            plotOptions: { bar: { horizontal: true, borderRadius: 5, barHeight: "60%" } },
            xaxis: { labels: { formatter: v => formatMetric(v, { charBudget: 6, decimals: 0 }), style: { fontSize: "10px", colors: "#9BA4B8" } } },
            yaxis: { labels: { style: { fontSize: "11px", colors: "#3A3F5C" } } },
            colors: ["#1B75BA"],
            tooltip: { y: { formatter: v => formatMetric(v, { charBudget: 0, decimals: 2 }) } },
          }} series={[{ name: "Income", data: (topCustomers || []).map(c => ({ x: c.name, y: c.amount })) }]} />
        </div>

        <div className="pcf-cell bg-white rounded-xl p-4" style={CARD} data-testid="top-vendors-chart">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-bold font-display" style={{ color: "#1C2040" }}>Top 10 Vendor Balances</h3>
              <DataSourceBadge source="quickbooks" />
            </div>
            <GraphExplainer title="Top 10 Vendors" text="Shows your largest vendor payment obligations. Longer bars = more spending. Use this to negotiate better terms with your top vendors or identify areas for cost reduction." />
          </div>
          <Chart type="bar" height={280} options={{
            ...CHART_3D,
            plotOptions: { bar: { horizontal: true, borderRadius: 5, barHeight: "60%" } },
            xaxis: { labels: { formatter: v => formatMetric(v, { charBudget: 6, decimals: 0 }), style: { fontSize: "10px", colors: "#9BA4B8" } } },
            yaxis: { labels: { style: { fontSize: "11px", colors: "#3A3F5C" } } },
            colors: ["#282560"],
            tooltip: { y: { formatter: v => formatMetric(v, { charBudget: 0, decimals: 2 }) } },
          }} series={[{ name: "Balance", data: (topVendors || []).map(v => ({ x: v.name, y: v.amount })) }]} />
        </div>
      </div>
    </div>
  );
}
